import { useEffect } from "react";
import { isWalkthroughRunning, noteReportSent } from "src/Pages/KnowledgeBase/kbSession";
import OutsideClickHandler from "react-outside-click-handler";

// Excel file icon shown next to the generated file name (40px tile).
const ExcelFileIcon = () => (
  <div className="w-10 h-10 rounded-[6.67px] bg-[#E5E8F3] flex items-center justify-center shrink-0">
    <svg width="14" height="18" viewBox="0 0 22 26" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 0.5H13L21 8.5V23.5C21 24.6046 20.1046 25.5 19 25.5H3C1.89543 25.5 1 24.6046 1 23.5V2.5C1 1.39543 1.89543 0.5 3 0.5Z"
        fill="#F3F4F8"
        stroke="#C9CEE2"
      />
      <path d="M13 0.5L21 8.5H14C13.4477 8.5 13 8.05228 13 7.5V0.5Z" fill="#C9CEE2" />
      <rect x="4" y="12" width="14" height="9" rx="1.5" fill="#15803D" />
      <path d="M7 14.5L11 18.5M11 14.5L7 18.5" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  </div>
);

const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 2.5V13M10 13L6 9M10 13L14 9M3.5 16.5H16.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SuccessCheck = () => (
  <div className="w-16 h-16 rounded-full bg-[#DCFCE7] flex items-center justify-center">
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="16" cy="16" r="15" stroke="#15803D" strokeWidth="2" />
      <path d="M10 16.5L14 20.5L22 12" stroke="#15803D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * "Report Generated" success modal. Shown after a report is generated and
 * emailed to recipients, with a button to download the generated file.
 *
 * shown / onClose: visibility control
 * fileUrl:  download URL for the generated file (Download button hidden if absent)
 * fileName: display name for the generated file
 * fileSize: optional size in bytes, rendered under the file name
 * description: optional override for the body copy
 */
const ReportGeneratedModal = ({
  shown,
  onClose,
  fileUrl,
  fileName = "Report.xlsx",
  fileSize,
  description = "Your report has been successfully generated and emailed to the selected recipients.",
}) => {
  /* only shown after a successful send — the walkthrough counts it here */
  useEffect(() => { if (shown) noteReportSent(); }, [shown]);
  if (!shown) return null;

  const sizeLabel = formatFileSize(fileSize);

  return (
    <div className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-40">
      <OutsideClickHandler disabled={isWalkthroughRunning()} onOutsideClick={onClose}>
        <div data-tour="rep-generated" className="w-[448px] max-w-[448px] max-h-[90vh] overflow-auto bg-white rounded-[10px] outline outline-1 -outline-offset-1 outline-[#F3F4F6] shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] flex flex-col">
          {/* Header / file section */}
          <div className="self-stretch pt-10 pb-8 px-8 flex flex-col items-center gap-1.5">
            <SuccessCheck />

            <div className="self-stretch pt-4 flex flex-col items-center">
              <p className="font-OpenSansSemiBold text-[24px] leading-[32px] text-[#1A2332] text-center capitalize">
                Report Generated
              </p>
            </div>

            <div className="self-stretch pb-4 flex flex-col items-center overflow-hidden">
              <p className="self-stretch font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#7C829A] text-center">
                {description}
              </p>
            </div>

            <div className="self-stretch flex items-center gap-3 p-4 rounded-[12px] bg-[#F8F9FE] outline outline-1 -outline-offset-1 outline-[#E5E8F3]">
              <ExcelFileIcon />
              <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                <span className="self-stretch font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#1A2332] truncate">
                  {fileName}
                </span>
                <span className="self-stretch font-OpenSansRegular text-[10px] leading-[14px] tracking-[0.10px] text-[#7C829A]">
                  Excel SpreadSheet{sizeLabel ? ` • ${sizeLabel}` : ""}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="self-stretch pb-8 px-8 flex flex-col items-start gap-3">
            {fileUrl && (
              <a
                href={fileUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="self-stretch h-12 px-6 rounded-[8px] bg-[#0065FD] flex items-center justify-center gap-2 font-OpenSansSemiBold text-[16px] leading-[24px] text-white capitalize hover:bg-[#0058E5]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M7.5 10V2C7.5 1.72386 7.72386 1.5 8 1.5C8.27614 1.5 8.5 1.72386 8.5 2V10C8.5 10.2761 8.27614 10.5 8 10.5C7.72386 10.5 7.5 10.2761 7.5 10Z"
                    fill="white"
                  />
                  <path
                    d="M1.5 12.6667V10C1.5 9.72386 1.72386 9.5 2 9.5C2.27614 9.5 2.5 9.72386 2.5 10V12.6667C2.5 12.8877 2.58786 13.0996 2.74414 13.2559C2.90042 13.4121 3.11232 13.5 3.33333 13.5H12.6667C12.8877 13.5 13.0996 13.4121 13.2559 13.2559C13.4121 13.0996 13.5 12.8877 13.5 12.6667V10C13.5 9.72386 13.7239 9.5 14 9.5C14.2761 9.5 14.5 9.72386 14.5 10V12.6667C14.5 13.1529 14.3067 13.6191 13.9629 13.9629C13.6191 14.3067 13.1529 14.5 12.6667 14.5H3.33333C2.8471 14.5 2.38093 14.3067 2.03711 13.9629C1.69329 13.6191 1.5 13.1529 1.5 12.6667Z"
                    fill="white"
                  />
                  <path
                    d="M10.9798 6.31315C11.1751 6.11789 11.4916 6.11789 11.6868 6.31315C11.8821 6.50841 11.8821 6.82492 11.6868 7.02018L8.35352 10.3535C8.15825 10.5488 7.84175 10.5488 7.64648 10.3535L4.31315 7.02018C4.11789 6.82492 4.11789 6.50841 4.31315 6.31315C4.50841 6.11789 4.82492 6.11789 5.02018 6.31315L8 9.29297L10.9798 6.31315Z"
                    fill="white"
                  />
                </svg>
                Download Excel
              </a>
            )}

            <button
              type="button"
              onClick={onClose}
              className="self-stretch h-12 px-6 rounded-[8px] bg-white outline outline-1 -outline-offset-1 outline-[#C9CEE2] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] flex items-center justify-center font-OpenSansSemiBold text-[16px] leading-[24px] text-[#1A2332] capitalize hover:bg-[#F7F9FC]"
            >
              Close
            </button>
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
};

export default ReportGeneratedModal;
